import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const Q2Page = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [chartVisible, setChartVisible] = useState(false);
  const navigate = useNavigate();

  const handleNextClick = () => {
    navigate('/q3');
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  useEffect(() => {
    setIsVisible(true);
    const timer = setTimeout(() => setChartVisible(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
              <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
                <i className="ri-heart-pulse-line text-white text-xl"></i>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">CF 생리학 연구</h1>
                <p className="text-sm text-gray-600">Life 마니또</p>
              </div>
            </Link>
            <nav className="flex items-center space-x-4">
              <Link to="/q1" className="text-gray-600 hover:text-gray-800 whitespace-nowrap">이전</Link>
              <button onClick={handleNextClick} className="text-green-600 hover:text-green-800 font-medium whitespace-nowrap">다음 질문</button>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-6 py-12">
        <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {/* Title Section */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Q2. 왜 폐 감염에 취약하고 숨쉬기 힘들까요?
            </h1>
            <p className="text-xl text-green-600 font-medium">
              끈끈한 점액이 폐의 '1차 방어선'을 무너뜨립니다.
            </p>
          </div>

          {/* Comparison Diagrams */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            {/* Normal Lung */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-green-700 mb-4 flex items-center">
                <i className="ri-check-circle-line mr-2"></i>
                정상 폐
              </h3>
              <div className="bg-green-50 rounded-lg p-6 mb-4">
                <div className="relative h-64 flex items-center justify-center">
                  {/* Simplified Normal Lung */}
                  <div className="relative w-48 h-48">
                    {/* Main lung shape */}
                    <div className="absolute inset-0 bg-pink-200 rounded-3xl border-4 border-pink-300"></div>
                    
                    {/* Moving Cilia - with smooth wave animation */}
                    <div className="absolute top-32 left-8 right-8 flex justify-around">
                      <div className="w-1 h-8 bg-green-600 rounded-full origin-bottom transform-gpu animate-bounce" style={{ animationDuration: '1.5s' }}></div>
                      <div className="w-1 h-8 bg-green-600 rounded-full origin-bottom transform-gpu animate-bounce" style={{ animationDuration: '1.3s', animationDelay: '0.2s' }}></div>
                      <div className="w-1 h-8 bg-green-600 rounded-full origin-bottom transform-gpu animate-bounce" style={{ animationDuration: '1.4s', animationDelay: '0.4s' }}></div>
                      <div className="w-1 h-8 bg-green-600 rounded-full origin-bottom transform-gpu animate-bounce" style={{ animationDuration: '1.6s', animationDelay: '0.1s' }}></div>
                      <div className="w-1 h-8 bg-green-600 rounded-full origin-bottom transform-gpu animate-bounce" style={{ animationDuration: '1.2s', animationDelay: '0.3s' }}></div>
                      <div className="w-1 h-8 bg-green-600 rounded-full origin-bottom transform-gpu animate-bounce" style={{ animationDuration: '1.5s', animationDelay: '0.5s' }}></div>
                    </div>
                    
                    {/* Thin mucus layer on top of cilia */}
                    <div className="absolute top-24 left-8 right-8 h-2 bg-gradient-to-r from-blue-300 via-blue-200 to-blue-300 rounded-full opacity-70">
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 rounded-full animate-pulse"></div>
                    </div>
                    
                    {/* Few bacteria on mucus - closer to mucus */}
                    <div className="absolute top-24 left-12 w-2 h-2 bg-red-400 rounded-full opacity-60"></div>
                    <div className="absolute top-24 right-16 w-2 h-2 bg-red-400 rounded-full opacity-60"></div>
                    
                    {/* Labels */}
                    <div className="absolute top-36 left-1/2 transform -translate-x-1/2 text-xs font-semibold text-green-700 bg-white px-2 py-1 rounded shadow-sm">
                      움직이는 섬모
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-700 text-center">
                섬모가 묽은 점액과 그 안의 세균을 밖으로 쉽게 밀어내는 모습
              </p>
            </div>

            {/* CF Patient Lung */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-red-700 mb-4 flex items-center">
                <i className="ri-close-circle-line mr-2"></i>
                CF 환자 폐
              </h3>
              <div className="bg-red-50 rounded-lg p-6 mb-4">
                <div className="relative h-64 flex items-center justify-center">
                  {/* Simplified CF Lung */}
                  <div className="relative w-48 h-48">
                    {/* Main lung shape - inflamed */}
                    <div className="absolute inset-0 bg-red-200 rounded-3xl border-4 border-red-400"></div>
                    
                    {/* Stuck Cilia - no movement */}
                    <div className="absolute top-32 left-8 right-8 flex justify-around">
                      <div className="w-1 h-8 bg-gray-500 rounded-full"></div>
                      <div className="w-1 h-8 bg-gray-500 rounded-full"></div>
                      <div className="w-1 h-8 bg-gray-500 rounded-full"></div>
                      <div className="w-1 h-8 bg-gray-500 rounded-full"></div>
                      <div className="w-1 h-8 bg-gray-500 rounded-full"></div>
                      <div className="w-1 h-8 bg-gray-500 rounded-full"></div>
                    </div>
                    
                    {/* Thick mucus layer on top of cilia */}
                    <div className="absolute top-24 left-8 right-8 h-4 bg-gradient-to-b from-yellow-600 via-yellow-500 to-yellow-600 rounded-lg opacity-90 shadow-inner">
                      <div className="absolute inset-0 bg-gradient-to-r from-orange-400 via-transparent to-orange-400 opacity-40 rounded-lg"></div>
                    </div>
                    
                    {/* Many bacteria on mucus - closer to mucus */}
                    <div className="absolute top-24 left-12 w-2 h-2 bg-red-600 rounded-full"></div>
                    <div className="absolute top-24 left-16 w-2 h-2 bg-red-600 rounded-full"></div>
                    <div className="absolute top-24 right-16 w-2 h-2 bg-red-600 rounded-full"></div>
                    <div className="absolute top-24 right-12 w-2 h-2 bg-red-600 rounded-full"></div>
                    <div className="absolute top-24 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-red-600 rounded-full"></div>
                    <div className="absolute top-24 left-20 w-2 h-2 bg-red-600 rounded-full"></div>
                    <div className="absolute top-24 right-20 w-2 h-2 bg-red-600 rounded-full"></div>
                    <div className="absolute top-25 left-14 w-2 h-2 bg-red-600 rounded-full"></div>
                    <div className="absolute top-25 right-14 w-2 h-2 bg-red-600 rounded-full"></div>
                    <div className="absolute top-25 left-1/3 w-2 h-2 bg-red-600 rounded-full"></div>
                    <div className="absolute top-25 right-1/3 w-2 h-2 bg-red-600 rounded-full"></div>
                    
                    {/* Labels */}
                    <div className="absolute top-36 left-1/2 transform -translate-x-1/2 text-xs font-semibold text-red-700 bg-white px-2 py-1 rounded shadow-sm">
                      갇힌 섬모
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-700 text-center">
                끈끈한 점액이 섬모에 엉겨 붙어 움직이지 않고, 세균이 그 안에 갇혀있는 모습
              </p>
            </div>
          </div>

          {/* Main Content */}
          <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              끈끈한 점액은 두 가지 큰 문제를 일으킵니다.
            </h2>

            <div className="space-y-8">
              {/* Problem 1 */}
              <div className="border-l-4 border-red-500 pl-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                  <span className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center mr-3 text-red-600 font-bold">1</span>
                  청소 기능 마비 (점액섬모 운반 능력 저하)
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  정상 폐는 섬모 운동을 통해 세균이나 이물질이 섞인 점액을 밖으로 밀어냅니다. 
                  하지만 CF 환자의 점액은 너무 끈끈해서, 섬모가 움직여도 점액이 밀려나가지 않고 기도에 달라붙습니다.
                </p>
              </div>

              {/* Problem 2 */}
              <div className="border-l-4 border-orange-500 pl-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                  <span className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center mr-3 text-orange-600 font-bold">2</span>
                  항균 기능 저하 (기도 표면의 산성화)
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Q1에서 보았듯 중탄산염 분비가 안되어 기도 표면이 산성화됩니다. 
                  이로 인해 우리 몸이 세균을 죽이기 위해 가진 '항균 물질'의 세균 사멸 기능이 크게 저하됩니다.
                </p>
              </div>
            </div>
          </div>

          {/* Results Section */}
          <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <i className="ri-alert-line text-red-600 mr-3"></i>
              결과: 만성 감염과 폐 손상
            </h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              방어선이 무너진 폐는 세균이 증식하기 좋은 환경이 됩니다. 이로 인해 황색포도상구균이나 녹농균 같은 세균에 의한 
              만성적인 감염이 반복됩니다. 지속적인 감염과 염증, 그리고 점액에 의한 기도 폐쇄는 폐 조직을 손상시키고(기관지확장증), 
              폐에 공기가 갇히는 '공기 걸림(air trapping)'을 유발하여 가스 교환을 방해합니다.
            </p>
          </div>

          {/* Chart Section */}
          <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              CF 환자 연령별 주요 감염균
            </h2>
            
            <div className={`transition-all duration-1000 ${chartVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
              <div className="max-w-2xl mx-auto">
                {/* Chart */}
                <div className="relative h-80 bg-gray-50 rounded-lg p-6">
                  {/* Y-axis */}
                  <div className="absolute left-4 top-6 bottom-16 w-px bg-gray-300"></div>
                  <div className="absolute left-0 top-6 text-xs text-gray-600">100%</div>
                  <div className="absolute left-0 top-20 text-xs text-gray-600">80%</div>
                  <div className="absolute left-0 top-32 text-xs text-gray-600">60%</div>
                  <div className="absolute left-0 top-44 text-xs text-gray-600">40%</div>
                  <div className="absolute left-0 top-56 text-xs text-gray-600">20%</div>
                  <div className="absolute left-0 bottom-16 text-xs text-gray-600">0%</div>
                  
                  {/* X-axis */}
                  <div className="absolute left-4 right-6 bottom-16 h-px bg-gray-300"></div>
                  
                  {/* Y-axis label - positioned at top left */}
                  <div className="absolute left-2 top-2 text-sm font-medium text-gray-700">
                    감염비율
                  </div>

                  {/* Bars */}
                  <div className="absolute left-16 right-6 bottom-16 top-6 flex items-end justify-center space-x-8">
                    {/* Children group */}
                    <div className="flex flex-col items-center space-y-2">
                      <div className="flex items-end space-x-2">
                        {/* S. aureus bar for children */}
                        <div className="relative group">
                          <div 
                            className="w-12 bg-blue-500 rounded-t transition-all duration-1000 hover:bg-blue-600 cursor-pointer"
                            style={{ height: chartVisible ? '120px' : '0px' }}
                          ></div>
                          <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                            황색포도상구균 80%
                          </div>
                        </div>
                        {/* P. aeruginosa bar for children */}
                        <div className="relative group">
                          <div 
                            className="w-12 bg-green-500 rounded-t transition-all duration-1000 hover:bg-green-600 cursor-pointer"
                            style={{ height: chartVisible ? '40px' : '0px', transitionDelay: '200ms' }}
                          ></div>
                          <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                            녹농균 25%
                          </div>
                        </div>
                      </div>
                      <div className="text-sm font-medium text-gray-700">어린이</div>
                    </div>
                    
                    {/* Adults group */}
                    <div className="flex flex-col items-center space-y-2">
                      <div className="flex items-end space-x-2">
                        {/* S. aureus bar for adults */}
                        <div className="relative group">
                          <div 
                            className="w-12 bg-blue-500 rounded-t transition-all duration-1000 hover:bg-blue-600 cursor-pointer"
                            style={{ height: chartVisible ? '60px' : '0px', transitionDelay: '400ms' }}
                          ></div>
                          <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                            황색포도상구균 40%
                          </div>
                        </div>
                        {/* P. aeruginosa bar for adults */}
                        <div className="relative group">
                          <div 
                            className="w-12 bg-green-500 rounded-t transition-all duration-1000 hover:bg-green-600 cursor-pointer"
                            style={{ height: chartVisible ? '140px' : '0px', transitionDelay: '600ms' }}
                          ></div>
                          <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                            녹농균 85%
                          </div>
                        </div>
                      </div>
                      <div className="text-sm font-medium text-gray-700">청소년 및 성인</div>
                    </div>
                  </div>
                </div>
                
                {/* Legend */}
                <div className="flex justify-center space-x-6 mt-4">
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 bg-blue-500 rounded"></div>
                    <span className="text-sm text-gray-700">황색포도상구균</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 bg-green-500 rounded"></div>
                    <span className="text-sm text-gray-700">녹농균</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center">
            <Link 
              to="/q1" 
              className="flex items-center space-x-2 bg-gray-100 hover:bg-gray-200 px-6 py-3 rounded-lg transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              <span>이전: Q1</span>
            </Link>
            
            <button 
              onClick={handleNextClick}
              className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg transition-colors whitespace-nowrap"
            >
              <span>다음: Q3</span>
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Q2Page;
